import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { useMyBetsQuery } from "../../../store/service/clientBetService";
import "./Client.scss";

const ClientMyBets = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState("sports");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("matched");
  const [sideFilter, setSideFilter] = useState("all");
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading, isFetching } = useMyBetsQuery(undefined, {
    pollingInterval: 3000,
  });

  const allBets = data?.data?.betList || [];

  const bets = useMemo(() => {
    let list = [...allBets];

    if (statusFilter === "matched") {
      // Current / matched = unsettled only (not won/lost/void)
      list = list.filter((b) => String(b.status || "").toLowerCase() === "open");
    } else if (statusFilter === "deleted") {
      list = list.filter((b) => b.status === "void" || b.status === "deleted" || b.status === "cancelled");
    }

    if (sideFilter === "back") list = list.filter((b) => b.betType === "back");
    if (sideFilter === "lay") list = list.filter((b) => b.betType === "lay");

    // Casino bets not separated in API yet — sports tab shows all non-casino naming
    if (tab === "casino") {
      list = list.filter((b) => /casino/i.test(String(b.matchName || b.marketType || "")));
    } else {
      list = list.filter((b) => !/casino/i.test(String(b.marketType || "")));
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((b) =>
        `${b.matchName} ${b.selection} ${b.marketType}`.toLowerCase().includes(q)
      );
    }

    return list.slice(0, pageSize);
  }, [allBets, statusFilter, sideFilter, tab, search, pageSize]);

  const totalAmount = bets.reduce((s, b) => s + (Number(b.stake) || 0), 0);

  return (
    <div className="w777-page">
      <h2 className="w777-page-title">Current Bets</h2>

      <div className="w777-search-row">
        <input
          type="search"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="w777-search-ico" aria-hidden="true">
          ⌕
        </span>
      </div>

      <div className="w777-underline-tabs">
        <button type="button" className={tab === "sports" ? "active" : ""} onClick={() => setTab("sports")}>
          Sports
        </button>
        <button type="button" className={tab === "casino" ? "active" : ""} onClick={() => setTab("casino")}>
          Casino
        </button>
      </div>

      <div className="w777-bet-filters">
        <div className="w777-radio-row">
          <label>
            <input
              type="radio"
              checked={statusFilter === "matched"}
              onChange={() => setStatusFilter("matched")}
            />
            Matched
          </label>
          <label>
            <input
              type="radio"
              checked={statusFilter === "deleted"}
              onChange={() => setStatusFilter("deleted")}
            />
            Deleted
          </label>
        </div>

        <div className="w777-table-tools">
          <label>
            Show{" "}
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
              {[10, 25, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>{" "}
            Entries
          </label>
          <div className="w777-export-icos" aria-hidden="true">
            <span>PDF</span>
            <span>XLS</span>
          </div>
        </div>

        <div className="w777-radio-row">
          {["all", "back", "lay"].map((s) => (
            <label key={s}>
              <input
                type="radio"
                checked={sideFilter === s}
                onChange={() => setSideFilter(s)}
              />
              {s === "all" ? "All" : s === "back" ? "Back" : "Lay"}
            </label>
          ))}
        </div>

        <div className="w777-bet-summary">
          Total Bets: {bets.length} &nbsp; Total Amount: {totalAmount.toFixed(0)}
        </div>
      </div>

      <div className="w777-data-table-wrap scroll-x">
        <table className="w777-data-table wide">
          <thead>
            <tr>
              <th>Sports</th>
              <th>Event Name</th>
              <th>Market Name</th>
              <th>Nation</th>
              <th>User Rate</th>
              <th>Amount</th>
              <th>Placed Date</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && bets.length === 0 ? (
              <tr>
                <td colSpan={7} className="w777-empty-cell">
                  <Spin />
                </td>
              </tr>
            ) : bets.length === 0 ? (
              <tr>
                <td colSpan={7} className="w777-empty-cell">
                  no records found
                </td>
              </tr>
            ) : (
              bets.map((bet) => (
                <tr
                  key={bet.betId}
                  className="clickable"
                  onClick={() => bet.matchId && nav(`/play/match/${bet.matchId}`)}>
                  <td>{/fancy/i.test(String(bet.marketType)) ? "Fancy" : bet.marketType || "Cricket"}</td>
                  <td>{String(bet.matchName || "").replace(/\s*\((Fancy|Bookmaker|Match Odds)\)\s*$/i, "")}</td>
                  <td>
                    {/fancy/i.test(String(bet.marketType))
                      ? "Session"
                      : bet.marketType === "match_odds"
                        ? "Match Odds"
                        : "Bookmaker"}
                  </td>
                  <td>
                    <strong>{bet.selection}</strong>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>
                      {bet.betType === "back"
                        ? /fancy/i.test(String(bet.marketType))
                          ? "YES"
                          : "LAGAI"
                        : /fancy/i.test(String(bet.marketType))
                          ? "NO"
                          : "KHAI"}
                    </div>
                  </td>
                  <td>
                    {/fancy/i.test(String(bet.marketType)) && bet.line != null ? (
                      <>
                        <div>Runs {bet.line}</div>
                        <div style={{ fontSize: 11, opacity: 0.8 }}>Rate {bet.odds}</div>
                      </>
                    ) : (
                      bet.odds
                    )}
                  </td>
                  <td>{bet.stake}</td>
                  <td>{bet.placedOn || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {isFetching && bets.length > 0 ? <div className="w777-refresh">Refreshing…</div> : null}
    </div>
  );
};

export default ClientMyBets;
